import { useAuth } from "@hooks/useAuth";
import { useHasAccess } from "@hooks/useHasAccess";
import { Icon } from "@iconify/react";
import { get, put } from "@services/api.service";
import {
  Button,
  Col,
  Dropdown,
  Grid,
  Image,
  InputRef,
  Layout,
  Menu,
  message,
  Modal,
  Result,
  Row,
  Space,
  Typography,
} from "antd";
import cs from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { corporateWebsiteUrl, eramanWebsiteUrl, magpWebsiteUrl, passengerWebsiteUrl } from "~/Utils";
import { Roles } from "~/Utils/Roles";
import { defaultRedirectURL, getCurrentModule } from "~/Utils/StringConstants";
import apssLogo from "../../Assets/SVG/Logos/APSS.svg";
import eramanLogo from "../../Assets/SVG/Logos/ERAMAN.svg";
import mahbLogo from "../../Assets/SVG/Logos/MAHBBig.svg";
import styles from "./Header.module.scss";

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface HeaderProps {
  toggle: boolean;
  onToggleCollapse: any;
}

const Modules: any = {
  PASSENGER: "Passenger",
  ERAMAN: "Eraman",
  MAGP: "MAGP",
  CORPORATE: "Corporate",
  MAHBCORP: "Corporate",
  EMPLOYEEAPP: "Employee App",
  APSS: "APSS",
};

const getDisplayModule = (module: any) => {
  return Modules[module] || module;
};

export const getModuleSpecificRoles = (allRoles: string[], currentModule: string) => {
  console.log("getModuleSpecificRoles", currentModule);
  if (allRoles.includes("SUPER_ADMIN")) {
    return ["SUPER_ADMIN"];
  }

  // Filter roles based on module
  const moduleRoleMap: { [key: string]: string[] } = {
    MAGP: ["MAGP_CREATOR", "MAGP_REVIEWER"],
    ERAMAN: ["ERAMAN_CREATOR", "ERAMAN_REVIEWER"],
    APSS: ["APSS_CREATOR", "APSS_REVIEWER"],
    EMPLOYEEAPP: ["EMPLOYEEAPP_CREATOR", "EMPLOYEEAPP_REVIEWER"],
    PASSENGER: ["PASSENGER_CREATOR", "PASSENGER_REVIEWER"],
    "PASSENGER BUTTERFLY EFFECT": ["PASSENGER_CREATOR", "PASSENGER_REVIEWER"],
    "PASSENGER ECMS": ["PASSENGER_ECMS_CREATOR", "PASSENGER_ECMS_REVIEWER"],
    CORPORATE: ["MAHB_CORPORATE_CREATOR", "MAHB_CORPORATE_REVIEWER"],
    CENTRALIZED: ["CENTRALIZED_CREATOR", "CENTRALIZED_REVIEWER"],
  };

  const moduleRoles = moduleRoleMap[currentModule] || [];
  return allRoles.filter((role) => moduleRoles.includes(role));
};

export const getDefaultRole = (moduleRoles: string[], currentModule: string) => {
  if (!moduleRoles.length) return "";
  const creatorRole = moduleRoles.find((role) => role.includes("CREATOR"));
  return creatorRole || moduleRoles[0];
};

export const Header: React.FC<HeaderProps> = ({ onToggleCollapse, toggle }) => {
  const [searchText, setSearchText] = useState("");
  const { user, logout } = useAuth();
  const [profileDetails, setProfileDetails] = useState<any>({});
  const [nameLogo, setNameLogo] = useState<string>();
  const [openModal, setOpenModal] = useState(false);
  const [, setUser] = useLocalStorage("user", {});
  const { lg, md, xs } = useBreakpoint();
  const { openToast } = useNotification();
  const navigate = useNavigate();
  const extras = useRef<HTMLDivElement>(null);
  const searchBar = useRef<InputRef>(null);
  const { hasRole } = useHasAccess();
  const [currentAirport] = useLocalStorage("currentAirport", null);
  const [localUser] = useLocalStorage("user", null);
  const module = getCurrentModule();
  const displayModule = getDisplayModule(module);
  const [currentRole, setCurrentRole] = useState(localUser.role);
  const airportList = [
    { label: "KLIA1", value: "klia1" },
    { label: "KLIA2", value: "klia2" },
    { label: "Langkawi", value: "langkawi" },
    { label: "Penang", value: "penang" },
    { label: "Kuching", value: "kuching" },
    { label: "Kota Kinabalu", value: "kota-kinabalu" },
  ];

  if (!user) {
    navigate("/auth/login");
  }

  const allRoles = localUser.access;
  const moduleSpecificRoles = getModuleSpecificRoles(allRoles, module);

  const getInitialRole = (currentModule: string, moduleRoles: string[], existingRole: string | null) => {
    const userString = localStorage.getItem("user");
    const user = userString ? JSON.parse(userString) : {};
    if (moduleRoles.length === 0) {
      delete user.role;
      localStorage.setItem("user", JSON.stringify(user));
      return "";
    }
    if (currentModule && !user.role) {
      const creatorRole = moduleRoles.find((role) => role.includes("CREATOR"));
      const selectedRole =
        currentModule === "CORPORATE"
          ? moduleRoles.find((role) => role === "MAHB_CORPORATE_CREATOR")
          : creatorRole || moduleRoles[0];

      if (selectedRole) {
        user.role = selectedRole;
        localStorage.setItem("user", JSON.stringify(user));
        updateCurrentRole(selectedRole);
        return selectedRole;
      }
    } else if (existingRole && moduleRoles.includes(existingRole)) {
      user.role = existingRole;
      localStorage.setItem("user", JSON.stringify(user));
      return existingRole;
    }
    return "";
  };

  useEffect(() => {
    const isModuleChange = !localStorage.getItem("currentModule");
    if (!isModuleChange) {
      getInitialRole(module, moduleSpecificRoles, localUser.role);
    }
  }, [module]);

  const updateCurrentRole = async (changeRole: string) => {
    console.log("selectedRole", changeRole);
    try {
      const payload = {
        uniqueId: "",
        currentRole: changeRole,
      };

      await put(`user/update/current-role`, payload);
      const updatedUser = { ...user, role: changeRole };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      const pathname = defaultRedirectURL(changeRole);
      navigate(pathname, { replace: true });

      window.location.reload();
    } catch (err: any) {
      message.error(err?.message || "Error updating role");
    }
  };

  const changeModule = async () => {
    try {
      if (localUser.role !== "SUPER_ADMIN") {
        const updates: any = {
          currentModule: null,
          user: { ...JSON.parse(localStorage.getItem("user") || "{}"), role: null },
        };
        localStorage.setItem("user", JSON.stringify(updates.user));
      }
      localStorage.removeItem("currentModule");
      localStorage.removeItem("currentAirport");
      navigate("/auth/dedicated/modules", { replace: true });
    } catch (error) {
      console.error("Error during module change:", error);
      openToast({
        type: "error",
        content: "Error switching modules. Please try again.",
      });
    }
  };
  const changeCMS = async () => {
    try {
      if (localUser.role !== "SUPER_ADMIN") {
        const updates: any = {
          currentModule: null,
          user: { ...JSON.parse(localStorage.getItem("user") || "{}"), role: null },
        };
        localStorage.setItem("user", JSON.stringify(updates.user));
      }
      localStorage.removeItem("currentModule");
      localStorage.removeItem("currentAirport");
      navigate("/auth/cms", { replace: true });
    } catch (error) {
      console.error("Error during module change:", error);
      openToast({
        type: "error",
        content: "Error switching modules. Please try again.",
      });
    }
  };
  const changeAirport = async () => {
    try {
      if (localUser.role !== "SUPER_ADMIN") {
        const updates: any = {
          currentModule: null,
          user: { ...JSON.parse(localStorage.getItem("user") || "{}"), role: null },
        };
        localStorage.setItem("user", JSON.stringify(updates.user));
      }
      localStorage.removeItem("currentAirport");
      navigate("/auth/dedicated/passenger/airport", { replace: true });
    } catch (error) {
      console.error("Error during module change:", error);
      openToast({
        type: "error",
        content: "Error switching modules. Please try again.",
      });
    }
  };

  useEffect(() => {
    if (user.email && !user.userName && user.role) {
      get(`/user/details/profile`)
        .then((res: any) => {
          if (res.response.data) {
            setUser({
              ...user,
              userLogo: res.response.data.userLogo,
              userName: res.response.data.userName,
            });
            setProfileDetails(res.response.data);
          }
        })
        .catch();
    } else if (user) {
      setProfileDetails({
        userLogo: user.userLogo,
        userName: user.userName,
      });
    }
  }, []);

  const getRole = (role: string) => {
    switch (role) {
      case Roles.MAGP_CREATOR:
        return "MAGP Creator";
      case Roles.MAGP_REVIEWER:
        return "MAGP Reviewer";
      case Roles.ERAMAN_CREATOR:
        return "Eraman Creator";
      case Roles.ERAMAN_REVIEWER:
        return "Eraman Reviewer";
      case Roles.APSS_CREATOR:
        return "APSS CREATOR";
      case Roles.APSS_REVIEWER:
        return "APSS Reviewer";
      case Roles.EMPLOYEEAPP_REVIEWER:
        return "Employee APP Reviewer";
      case Roles.EMPLOYEEAPP_CREATOR:
        return "Employee APP Creator";
      // case Roles.MAHBCOMM_CREATOR:
      //   return "MAHB Commercial Creator";
      // case Roles.MAHBCOMM_REVIEWER:
      //   return "MAHB Commercial Reviewer";
      case Roles.PASSENGER_CREATOR:
        return "Passenger Butterfly Creator";
      case Roles.PASSENGER_REVIEWER:
        return "Passenger Butterfly Reviewer";
      case Roles.PASSENGER_ECMS_CREATOR:
        return "Passenger ECMS Creator";
      case Roles.PASSENGER_ECMS_REVIEWER:
        return "Passenger ECMS Reviewer";
      case Roles.MAHB_CORPORATE_CREATOR:
        return "Corporate Creator";
      case Roles.MAHB_CORPORATE_REVIEWER:
        return "Corporate Reviewer";
      case Roles.SUPER_ADMIN:
        return "Super Admin";
      case Roles.CENTRALIZED_CREATOR:
        return "Centralized Creator";
      case Roles.CENTRALIZED_REVIEWER:
        return "Centralized Reviewer";
      default:
        return "";
    }
  };

  const getModuleUrl = (module: string) => {
    switch (module) {
      case "PASSENGER BUTTERFLY EFFECT":
        return passengerWebsiteUrl;
      case "PASSENGER ECMS":
        return passengerWebsiteUrl;
      case "PASSENGER":
        return passengerWebsiteUrl;
      case "MAGP":
        return magpWebsiteUrl;
      case "ERAMAN":
        return eramanWebsiteUrl;
      case "CORPORATE":
        return corporateWebsiteUrl;
      default:
        return magpWebsiteUrl;
    }
  };

  const getLogo = (role: string, module: string) => {
    switch (role) {
      case Roles.MAGP_CREATOR:
      case Roles.MAGP_REVIEWER:
        return mahbLogo;

      case Roles.ERAMAN_CREATOR:
      case Roles.ERAMAN_REVIEWER:
        return eramanLogo;

      case Roles.APSS_CREATOR:
      case Roles.APSS_REVIEWER:
        return apssLogo;

      case Roles.SUPER_ADMIN:
        if (module === "MAGP") {
          return mahbLogo;
        } else if (module === "ERAMAN") {
          return eramanLogo;
        } else {
          return mahbLogo;
        }
      default:
        return mahbLogo;
    }
  };

  if (!user) {
    navigate("/auth/login");
  }

  useEffect(() => {
    if (profileDetails?.userName || user) {
      const nameArray = profileDetails?.userName?.split(" ");
      const firstName = nameArray?.[0]?.charAt(0) || "";
      const lastName = nameArray?.length > 1 ? nameArray?.[nameArray?.length - 1]?.charAt(0) : "";

      const displayText = `${firstName}${lastName}`.toUpperCase() || user?.name?.charAt(0)?.toUpperCase();
      console.log("displayText", displayText);
      setNameLogo(displayText);
    }
  }, [profileDetails, user]);

  return (
    <Layout.Header
      className={cs(styles.header, {
        [styles.SmallHeader]: xs,
      })}
    >
      <Row
        align="middle"
        gutter={[16, 16]}
        style={{ height: "100%", marginLeft: "-28px" }}
      >
        <Col
          span={2}
          className={styles.logoStyle}
          // style={{
          //   ...(toggle
          //     ? {
          //         display: "flex",
          //         alignItems: "center",
          //       }
          //     : {}),
          // }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <Image
            src={getLogo(currentRole, module)} //user?.role
            preview={false}
            style={{
              maxWidth: 100,
              height: "50px",
              objectFit: "contain",
              // marginRight:'-10px'
            }}
          />
          {/* {
            toggle ? <Image
            src={getLogo(user?.role, module)}
            preview={false}
            style={{
              maxWidth: 100, 
              height: '50px', 
              objectFit: 'contain', 
              // marginRight:'-10px'
            }}
          /> : 
          <h1 className="font-bold text-2xl">{displayModule}</h1>
          } */}
        </Col>
        {profileDetails?.userName && (
          <Col span={1}>
            <Icon
              cursor={"pointer"}
              height={30}
              icon={toggle ? "bx:menu-alt-left" : "material-symbols:close"}
              onClick={() => {
                const collapsed = localStorage.getItem("collapsed");
                if (collapsed === "true") {
                  localStorage.setItem("collapsed", "false");
                  onToggleCollapse(false);
                } else {
                  localStorage.setItem("collapsed", "true");
                  onToggleCollapse(true);
                }
              }}
            />
          </Col>
        )}
        <Col
          span={9}
          style={{ display: "flex" }}
        >
          {(module === "PASSENGER" || module === "PASSENGER ECMS") && (
            <Typography.Text className="!text-[#712b90] text-lg font-bold">
              {airportList.find((a) => a.value === currentAirport)?.label || currentAirport}
            </Typography.Text>
          )}
        </Col>

        <Col
          span={12}
          style={{ textAlign: "right", justifyContent: "space-between" }}
        >
          <div
            className={styles.NavExtras}
            ref={extras}
            style={{
              marginLeft: "auto",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              lineHeight: 1,
              gap: 16,
            }}
          >
            {/* <Divider
              type="vertical"
              style={{
                borderLeft: "1px solid rgba(0, 0, 0, 0.22)",
                background: "D7D5F1",
                height: "56px",
              }}
            /> */}
            {profileDetails?.userName && module !== "CENTRALIZED" && (
              <Button
                onClick={() => {
                  const url = getModuleUrl(module);
                  window.open(url, "_blank");
                }}
                style={{ marginRight: "50px" }}
              >
                View Site
              </Button>
            )}

            <Typography.Link>
              <Dropdown
                overlay={
                  <Menu>
                    {!lg && (
                      <Menu.Item
                        key="profile"
                        disabled
                      >
                        <Space
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            lineHeight: 1,
                            textAlign: "left",
                            alignItems: "start",
                            justifyContent: "center",
                          }}
                          size={4}
                        >
                          <Text
                            strong
                            className={styles.userName}
                            ellipsis
                          >
                            {profileDetails && profileDetails.userName}
                          </Text>
                          <Text
                            type="secondary"
                            strong={false}
                            ellipsis
                          >
                            {getRole(currentRole)}
                            {/* user?.role */}
                          </Text>
                        </Space>
                      </Menu.Item>
                    )}
                    {moduleSpecificRoles.map((item: any) => {
                      return (
                        <Menu.Item
                          key={item}
                          disabled={item == currentRole}
                          onClick={() => updateCurrentRole(item)}
                        >
                          {item == currentRole ? getRole(item) + " (In Use)" : getRole(item)}
                        </Menu.Item>
                      );
                    })}
                    {/* { user?.role === 'SUPER_ADMIN' && (<Menu.Item
                      key="changeModule"
                      onClick={() => changeModule()}
                    >
                      Change Module
                    </Menu.Item>)
                    } */}
                    {module !== "CENTRALIZED" && (
                      <Menu.Item
                        key="changeModule"
                        onClick={() => changeModule()}
                      >
                        Change Module
                      </Menu.Item>
                    )}
                    {(module === "PASSENGER" || module === "PASSENGER ECMS") && (
                      <Menu.Item
                        key="changeAirport"
                        onClick={() => changeAirport()}
                      >
                        Change Airport
                      </Menu.Item>
                    )}
                    {/* <Menu.Item
                      key="changeCMS"
                      onClick={() => changeCMS()}
                    >
                      Change CMS
                    </Menu.Item> */}
                    <Menu.Item
                      key="logout"
                      onClick={() => logout()}
                    >
                      Logout
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <Space
                  size={"small"}
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <Text className="userNameLogo">{nameLogo}</Text>
                  {lg && (
                    <Space
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        lineHeight: 1,
                        textAlign: "left",
                        alignItems: "start",
                        justifyContent: "center",
                      }}
                      size={4}
                    >
                      <Text
                        strong
                        className={styles.userName}
                        ellipsis
                      >
                        {/* {profileDetails && profileDetails.userName} */}
                        {profileDetails.userName ? profileDetails.userName : user && user.name && user.name}
                      </Text>
                      <Text
                        type="secondary"
                        strong={false}
                        ellipsis
                      >
                        {getRole(currentRole)}
                        {/* user?.role */}
                      </Text>
                    </Space>
                  )}
                  <Icon
                    icon="ep:arrow-down-bold"
                    height="20"
                    style={{ margin: "auto", color: styles.strawberry }}
                  />
                </Space>
              </Dropdown>
            </Typography.Link>
          </div>
        </Col>
      </Row>
      <Modal
        open={openModal}
        maskClosable={false}
        centered
        footer={null}
        closable={false}
      >
        <Result
          status={"403"}
          title={"Role Update Detected!"}
          subTitle={
            <Typography.Text>
              Your role has been modified to {currentRole && getRole(currentRole)} Certain modules may now be
              inaccessible. To access your newly assigned roles, please click on “Update Role.” If you require
              assistance or clarification, kindly reach out to your administrator for support through{" "}
              <b>clmsupport@myairports.com</b>
            </Typography.Text>
          }
          extra={[
            <div style={{ gap: 10, display: "flex", justifyContent: "center" }}>
              <Button
                type="primary"
                onClick={() => {
                  if (currentRole) {
                    updateCurrentRole(currentRole);
                  }
                }}
              >
                Update Role
              </Button>
              <Button
                type="ghost"
                onClick={() => {
                  if (currentRole) {
                    logout();
                  }
                }}
              >
                Logout
              </Button>
            </div>,
          ]}
        />
      </Modal>
    </Layout.Header>
  );
};
